import React, { useEffect, useState } from "react";
import { Download, X, Share, WifiOff, RefreshCw } from "lucide-react";
import { Btn } from "@/components/kit";
import {
  isStandalone, isIOS, onInstallAvailability, promptInstall,
  onUpdateReady, applyUpdate,
} from "@/lib/pwa";

const UAT = process.env.REACT_APP_ENABLE_DEV_LOGIN === "true";

// Small, unobtrusive internal UAT marker. Never present in a production build
// (dev-login is disabled in production).
export function UatBadge() {
  if (!UAT) return null;
  return (
    <div data-testid="uat-badge"
      className="fixed bottom-2 left-2 z-[60] pointer-events-none select-none font-mono text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-[3px] bg-[hsl(var(--vda-clay))]/15 text-[hsl(var(--vda-clay))]">
      UAT
    </div>
  );
}

// Global offline banner. Uses only connectivity state — never renders stale data.
export function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  if (!offline) return null;
  return (
    <div data-testid="offline-banner"
      className="fixed top-0 inset-x-0 z-[70] bg-[hsl(var(--vda-amber-bg))] border-b border-[hsl(var(--vda-amber))]/30 text-[hsl(var(--vda-amber))]">
      <div className="max-w-3xl mx-auto px-4 py-2 flex items-center gap-2 text-sm">
        <WifiOff className="h-4 w-4 flex-none" />
        <span className="flex-1"><strong>You're offline.</strong> Reconnect to see the latest vehicle and booking information.</span>
        <button data-testid="offline-retry" onClick={() => window.location.reload()} className="inline-flex items-center gap-1 font-medium hover:underline">
          <RefreshCw className="h-3.5 w-3.5" /> Retry
        </button>
      </div>
    </div>
  );
}

// Watches for a newer service worker and offers a restrained refresh.
export function PwaUpdateWatcher() {
  const [worker, setWorker] = useState(null);
  useEffect(() => onUpdateReady((w) => setWorker(w)), []);
  if (!worker) return null;
  return (
    <div data-testid="pwa-update-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 bg-foreground text-background rounded-[8px] px-4 py-2.5 shadow-lg">
      <span className="text-sm">relay. has been updated.</span>
      <button data-testid="pwa-refresh" onClick={() => applyUpdate(worker)}
        className="inline-flex items-center gap-1 text-sm font-semibold bg-background text-foreground rounded-[6px] px-3 py-1">
        <RefreshCw className="h-3.5 w-3.5" /> Refresh
      </button>
      <button aria-label="Dismiss update" onClick={() => setWorker(null)} className="opacity-70 hover:opacity-100"><X className="h-4 w-4" /></button>
    </div>
  );
}

// Quiet "Install relay." action for staff (account/help area). Renders nothing
// when already installed or when the browser cannot offer installation.
export function StaffInstallButton() {
  const [canInstall, setCanInstall] = useState(false);
  useEffect(() => onInstallAvailability(setCanInstall), []);
  if (isStandalone() || !canInstall) return null;
  return (
    <button data-testid="staff-install-btn" onClick={() => promptInstall()} title="Install relay."
      className="p-2 hover:bg-secondary rounded-[6px] text-muted-foreground hover:text-foreground">
      <Download className="h-4 w-4" />
    </button>
  );
}

// Driver install prompt — shown ONCE per session after the driver reaches home.
// Android/Chromium: real install. iOS: short Add to Home Screen guide.
export function DriverInstallPrompt() {
  const [canInstall, setCanInstall] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("relay_install_dismissed") === "1"
  );
  const [showIosGuide, setShowIosGuide] = useState(false);
  useEffect(() => onInstallAvailability(setCanInstall), []);

  if (isStandalone() || dismissed) return null;
  const ios = isIOS();
  if (!canInstall && !ios) return null; // nothing useful to offer

  const close = () => { sessionStorage.setItem("relay_install_dismissed", "1"); setDismissed(true); };

  return (
    <div data-testid="driver-install-prompt"
      className="mt-6 border border-[hsl(var(--vda-strong-border))] rounded-[8px] bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Keep relay. on your phone</h3>
          <p className="text-xs text-muted-foreground mt-1">Add relay. to your home screen for quicker access to bookings and updates.</p>
        </div>
        <button data-testid="driver-install-dismiss" onClick={close} className="p-1 text-muted-foreground hover:text-foreground" aria-label="Dismiss"><X className="h-4 w-4" /></button>
      </div>

      {ios && !canInstall ? (
        <div className="mt-3">
          {!showIosGuide ? (
            <Btn variant="outline" className="w-full" data-testid="driver-install-ios" onClick={() => setShowIosGuide(true)}>
              <Share className="h-4 w-4" /> Add relay. to Home Screen
            </Btn>
          ) : (
            <ol className="mt-1 space-y-1.5 text-xs text-muted-foreground list-decimal list-inside" data-testid="ios-install-steps">
              <li>Tap the <strong>Share</strong> icon in Safari.</li>
              <li>Tap <strong>Add to Home Screen</strong>.</li>
              <li>Tap <strong>Add</strong> to confirm.</li>
            </ol>
          )}
        </div>
      ) : (
        <Btn className="w-full mt-3" data-testid="driver-install-btn"
          onClick={async () => { const r = await promptInstall(); if (r === "accepted") close(); }}>
          <Download className="h-4 w-4" /> Install relay.
        </Btn>
      )}
    </div>
  );
}
